/**
 * One Android Auto tap-to-call reaches JS twice when the phone is backgrounded
 * (2026-10-20): the live `carStartCall` event, and the SharedPreferences copy
 * native saved for a dead WebView, drained on the next foreground. The second
 * start threw "Already in a call" and the car showed "Can't start call —
 * You're already on a call" after the driver hung up. A tap is identified by
 * its peer + tap time (`ts`, epoch ms) on both paths.
 */

/** Remembers recently handled car taps; `claim` is true only for the first delivery. */
export function createCarStartTapLedger(limit = 16) {
  const seen: string[] = []
  return {
    claim(peerId: string, tapTs: number | undefined): boolean {
      // Pre-105 shells send no tap time: nothing to dedupe on.
      if (!tapTs) return true
      const key = `${peerId}:${tapTs}`
      if (seen.includes(key)) return false
      seen.push(key)
      if (seen.length > limit) seen.shift()
      return true
    },
    reset(): void {
      seen.length = 0
    },
  }
}

type LiveCallLike = {
  phase: string
  calleeId?: string | null
  peer?: { userId?: string | null } | null
  carTapAt?: number | null
} | null

/**
 * A car start that failed with "Already in a call" because the live call IS
 * that car dial (same tap, or the car's own dial to the same peer): a
 * re-delivery, never a reason to alert the driver.
 */
export function isDuplicateOfLiveCarDial(
  session: LiveCallLike,
  peerId: string,
  tapTs: number | undefined,
): boolean {
  if (!session || session.phase === 'ended' || session.phase === 'idle') return false
  if (session.carTapAt == null) return false
  if (tapTs && session.carTapAt === tapTs) return true
  const livePeer = session.peer?.userId ?? session.calleeId ?? null
  return livePeer === peerId
}
